import { useEffect } from "react";

import { useNavigate } from "@Libs/Router/hooks/useNavigate";
import { useSearchParams } from "@Libs/Router/hooks/useSearchParams";

import { ALLOWED_P_CATEGORY_IDS } from "../config/constants";
import { useTaskCreationFlow } from "../Contexts/TaskCreationFlow";
import { findPCategoryBySCategory } from "../services/categories";
import { useCategoryTree } from "./useCategoryTree";
import { useDraftIdentity } from "./useDraftIdentity";

export interface IFlowPCategory {
  /** `undefined` when no source names a theme. */
  pCategoryId: number | undefined;
  /**
   * True while the theme cannot be decided yet. Nothing may eject the visitor
   * on an unresolved theme while this is set — it is "not known yet", not
   * "not there".
   */
  isResolving: boolean;
  /** True when a theme is resolved *and* this flow handles it. */
  isSupported: boolean;
}

/**
 * The category theme the flow is running under (RT-4321).
 *
 * There is no single source, so this ranks the three there are:
 *
 * 1. **The draft's own `p_category_id`.** Null until a subcategory is picked —
 *    a task has no `p_category` column and the serializer resolves it through
 *    `s_category` — so this answers only in the back half of the flow, but is
 *    authoritative once present.
 * 2. **The draft's `s_category_id`, resolved through the category tree.** The
 *    SEM pages send `s_category` with no theme beside it
 *    (`app/helpers/shared_helper.rb` sends one or the other, never both), so
 *    for those visitors this is the only source that ever answers.
 * 3. **The URL.** The fallback before a draft exists. Once the backend has
 *    answered, a stale history entry must not override it.
 *
 * Ranked rather than defaulted. A `DEFAULT_P_CATEGORY_ID` used to stand in
 * whenever the chain came up empty, which silently relabelled the job as
 * Bricolage — harmless only for as long as Bricolage is the sole entry in
 * `ALLOWED_P_CATEGORY_IDS`, and actively wrong the day a second theme is
 * added. An unresolved theme now reads as unresolved, and
 * `useFlowThemeGuard` sends the visitor out of the flow rather than guessing.
 */
export const useFlowPCategory = (): IFlowPCategory => {
  const { draft, isFlowLoading } = useTaskCreationFlow();
  const pCategory = useSearchParams("p_category");
  const { tree, isLoading: isTreeLoading } = useCategoryTree();

  // A junk param has to read as absent: `Number("abc")` is NaN, which would
  // otherwise sail through as a theme id and reach the backend as one.
  const fromUrl = pCategory ? Number(pCategory) : undefined;
  const derived =
    tree && draft?.s_category_id
      ? findPCategoryBySCategory(tree, draft.s_category_id)?.id
      : undefined;

  const pCategoryId =
    draft?.p_category_id ??
    derived ??
    (Number.isFinite(fromUrl) ? fromUrl : undefined);

  return {
    pCategoryId,
    isResolving:
      pCategoryId === undefined && (isFlowLoading || isTreeLoading),
    isSupported:
      pCategoryId !== undefined && ALLOWED_P_CATEGORY_IDS.includes(pCategoryId),
  };
};

/**
 * The category context every URL inside the flow carries (RT-4321).
 *
 * `navigate` builds a path from `params` alone, so a param not forwarded dies
 * at that hop: step-screen URLs are just `/:screenKey`, and the theme the
 * Rails picker appended lived on the description and subcategory URLs only.
 * One hop into the steps and it was gone.
 *
 * `s_category` is deliberately *not* here: inside the steps its presence is
 * the SEM-entry marker (`DynamicScreen` renders the prefill banner off it, and
 * `ServiceRequestCreationRoot` skips the picker off it), so carrying it
 * forward would relabel every ordinary screen as a SEM entry.
 *
 * Empty when no theme resolves, rather than falling back to a default — see
 * `useFlowPCategory`. `useFlowThemeGuard` is what acts on that.
 *
 * Empty in edit mode too: the request already has its subcategory, so the
 * param has nothing left to do there — it only meant something as the SEM
 * entry's theme for the create flow.
 */
export const useFlowCategoryParams = (): { p_category?: string } => {
  const { pCategoryId } = useFlowPCategory();
  const { isEditing } = useDraftIdentity();

  return pCategoryId === undefined || isEditing
    ? {}
    : { p_category: String(pCategoryId) };
};

/**
 * Sends the visitor out to the Rails category picker when the flow cannot run
 * for their theme (RT-4321).
 *
 * Mounted once in `TaskCreationShell` so it covers every screen. It used to
 * live in `DescriptionScreen` alone, which meant a step screen under an
 * unsupported theme — or under no resolvable theme at all — rendered happily:
 * `/service_request/new/task_type?p_category=9` was never checked.
 *
 * `isResolving` is the whole reason this is not a one-liner. Ejecting on a
 * theme that is merely *not known yet* would throw a SEM visitor out mid-fetch
 * of the category tree, so the guard waits for a decision before acting on it.
 */
export const useFlowThemeGuard = (): IFlowPCategory => {
  const navigate = useNavigate();
  const flowPCategory = useFlowPCategory();
  const { pCategoryId, isResolving, isSupported } = flowPCategory;

  useEffect(() => {
    if (isResolving || isSupported) return;

    // `reload`, because the picker is a Rails page rather than a route of
    // this widget. `replace`, so the entry the visitor is being taken off
    // does not sit in their history waiting to be stepped back into.
    navigate("REQUEST_SERVICE", {
      withLocale: true,
      reload: true,
      replace: true,
    });
  }, [pCategoryId, isResolving, isSupported]);

  return flowPCategory;
};
